import React, { useState } from 'react';
import { ArrowRight, Check, Scissors } from 'lucide-react';
import toast from 'react-hot-toast';
import { useBannerClick, useBannerImpression } from './adPreview';
import type { PromoControls } from './adBanners';
import './DeltaBikePromoBanner.css';

const SLOT = 'delta-bike';
const DELTA_BIKE_URL = 'https://tidd.ly/4dIipNu';
/** Exklusiv für Radsport-Events: schaltet im Shop den „Bestpreis" frei, den ohne Code niemand sieht. */
export const DELTA_BIKE_CODE = 'KLK6-DFEL-HFR8-KECH';

/**
 * Kopiert den Code im selben Klick, der den Shop öffnet: der Aufruf muss synchron im
 * Klick-Handler starten, sonst verweigert Safari die Zwischenablage. Ohne Clipboard-API
 * (alte Browser, unsicherer Kontext) über ein unsichtbares Textfeld.
 */
function copyCode(): Promise<void> {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(DELTA_BIKE_CODE);
  return new Promise((resolve, reject) => {
    const field = document.createElement('textarea');
    field.value = DELTA_BIKE_CODE;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(field);
    if (ok) resolve(); else reject(new Error('copy'));
  });
}

/**
 * Die ganze Fläche ist **ein** Link. Pfeile und Punkte des Werbeplatzes (`controls`) liegen
 * deshalb nicht darin, sondern als Geschwister in einer gemeinsamen Hülle darüber: ein Knopf im
 * Link wäre verschachtelt, und ein Klick aufs Blättern öffnete den Shop.
 *
 * Der Klick kopiert zugleich den Rabattcode; der Code steht trotzdem lesbar im Banner, falls die
 * Zwischenablage nicht mitspielt oder jemand ihn abtippen will.
 */
export default function DeltaBikePromoBanner({ controls }: { controls?: PromoControls }) {
  useBannerImpression(SLOT);
  const trackClick = useBannerClick(SLOT);
  const [copied, setCopied] = useState(false);

  const handleClick = (variant: 'desktop' | 'mobile') => {
    trackClick(`landing-${variant}`);
    copyCode()
      .then(() => {
        setCopied(true);
        toast.success('Code kopiert. Beim Bezahlen bei Delta Bike einfügen.', { id: 'delta-code' });
      })
      .catch(() => toast(`Dein Code: ${DELTA_BIKE_CODE}`, { id: 'delta-code' }));
  };

  return <>
    {(['desktop', 'mobile'] as const).map(variant => (
      <div key={variant} className={`delta-promo-wrap ${variant === 'desktop' ? 'hide-mobile' : 'hide-desktop'}`}>
        <a className={`delta-promo delta-promo--${variant}${controls ? ' delta-promo--with-controls' : ''}`}
          href={DELTA_BIKE_URL} target="_blank" rel="noopener noreferrer nofollow sponsored"
          aria-label={`Delta Bike: Bestpreis mit Code ${DELTA_BIKE_CODE}. Kopiert den Code und öffnet den Shop in einem neuen Tab`}
          onClick={() => handleClick(variant)}>
          <div className="delta-promo-brand">
            <img src="/partners/delta-bike.svg" alt="Delta Bike" width="118" height="24" />
            <span>Radsport. Aus Leidenschaft.</span>
          </div>
          <div className="delta-promo-copy">
            <div className="delta-promo-eyebrow">NUR ÜBER RADSPORT-EVENTS</div>
            <div className="delta-promo-headline">Dein Equipment zum Bestpreis.</div>
          </div>
          <span className="delta-promo-ad">ANZEIGE</span>
          <span className={`delta-promo-action${copied ? ' is-copied' : ''}`}>
            <Scissors className="delta-promo-scissors" size={18} aria-hidden />
            <span className="delta-promo-code">
              {DELTA_BIKE_CODE}
              {copied && <span className="delta-promo-code-done"><Check size={14} aria-hidden />KOPIERT</span>}
            </span>
            <span className="delta-promo-cta">
              {/* Kurz in der flachen Fassung zwischen den Event-Zeilen (Handy). */}
              <span className="delta-promo-cta-lang">Code kopieren und zum Shop</span>
              <span className="delta-promo-cta-kurz">Zum Shop</span>
              <ArrowRight size={16} aria-hidden />
            </span>
          </span>
        </a>
        {controls?.(variant)}
      </div>
    ))}
  </>;
}
